import {
  EKycVerificationStatus,
  IdType,
  ExamineeRegistrationStatus,
  useGetExamEventExamineeByIdQuery,
} from '@exam-center/graphql';
import { createContext, useMemo } from 'react';

import type {
  ProctoringRegistrationDetailsContextValue,
  ProctoringRegistrationDetailsProviderProps,
} from './types';

const ProctoringRegistrationDetailsContext = createContext<
  ProctoringRegistrationDetailsContextValue | undefined
>(undefined);

export default function ProctoringRegistrationDetailsProvider(
  props: Readonly<ProctoringRegistrationDetailsProviderProps>,
) {
  const { children, examCenterCode, examEventId, examEventExamineeId } = props;

  const { data, loading } = useGetExamEventExamineeByIdQuery({
    variables: {
      getExamEventExamineeByIdInput: {
        id: examEventExamineeId,
      },
    },
  });

  const examEventExaminee = data?.ecGetExamEventExamineeById.examEventExaminee;
  const examinee = examEventExaminee?.examinee;

  const contextValue: ProctoringRegistrationDetailsContextValue = useMemo(() => {
    return {
      examinee: {
        title: examinee?.title ?? '',
        firstName: examinee?.firstName ?? '',
        middleName: examinee?.middleName ?? null,
        lastName: examinee?.lastName ?? '',
        eKycStatus: examinee?.eKycStatus ?? EKycVerificationStatus.NotStarted,
        idNumber: examinee?.idNumber ?? '',
        idType: examinee?.idType ?? IdType.CitizenCard,
        dateOfBirth: examinee?.dateOfBirth ?? null,
        expiredDate: examinee?.eKycExpiredAt ?? null,
        countryCode: examinee?.countryCode ?? null,
      },
      examEventExaminee: {
        id: examEventExaminee?.id ?? '',
        status: examEventExaminee?.registrationStatus ?? ExamineeRegistrationStatus.NotRegistered,
        seatNumber: examEventExaminee?.seatNumber?.toString() ?? '',
        registrationStatus:
          examEventExaminee?.registrationStatus ?? ExamineeRegistrationStatus.NotRegistered,
        registrationType: examEventExaminee?.registrationType ?? null,
        registrationDocumentType: examEventExaminee?.registrationDoc?.type ?? null,
        reason: examEventExaminee?.reason ?? '',
      },
      examCenterCode,
      examEventId,
      examEventExamineeId,
      loading,
    };
  }, [examCenterCode, examEventId, examEventExamineeId, examinee, examEventExaminee, loading]);

  return (
    <ProctoringRegistrationDetailsContext.Provider value={contextValue}>
      {children}
    </ProctoringRegistrationDetailsContext.Provider>
  );
}

export { ProctoringRegistrationDetailsContext };
